<script setup lang="ts">
import { computed, onUnmounted, ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  ContentType,
  SortOption,
  type SearchParams,
} from '@/api/apiClient/search';
import NavDropdown from '@/components/header/NavDropdown.vue';
import { useAuthStore } from '@/features/auth/authStore';
import { useConfigStore } from '@/features/config/configStore';
import { useSearchLoadingStore } from '@/features/search/searchLoadingStore';
import { useSearchQueryString } from '@/features/search/useSearchQueryString';
import { useSkillsetsStore } from '@/features/skillsets/skillsetsStore';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const configStore = useConfigStore();
const skillsetsStore = useSkillsetsStore();
const searchLoadingStore = useSearchLoadingStore();

const searchTextQuery = ref('');
let searchTimeout: number | null = null;

onUnmounted(() => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
});

const isAuthenticated = computed(() => authStore.isAuthenticated);
const userDisplayName = computed(() => {
  return authStore.user?.displayName ?? authStore.user?.email ?? 'Anonymous';
});
const userProfileUrl = computed(() => {
  const email = authStore.user?.email;
  return email ? `/users/${encodeURIComponent(email)}` : '/login';
});

const showAdminLinks = computed(
  () =>
    isAuthenticated.value &&
    configStore.isPrivateInstance &&
    authStore.isAdmin,
);
const showSkillsetsLink = computed(
  () => !configStore.isPrivateInstance || isAuthenticated.value,
);
const showSkillsLink = computed(
  () => !configStore.isPrivateInstance || isAuthenticated.value,
);
const showSubagentsLink = computed(
  () => !configStore.isPrivateInstance || isAuthenticated.value,
);
const showInternalRegistryLink = computed(
  () => !configStore.isPrivateInstance,
);

/**
 * Handle search with debouncing
 */
const handleSearchInput = () => {
  if (route.name !== 'search') {
    return;
  }

  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = window.setTimeout(handleSearch, 300);
};

const handleSearch = () => {
  const hadText = (searchQuery.value.text ?? '').trim() !== '';
  const hasText = searchTextQuery.value.trim() !== '';
  const params: SearchParams = {
    ...searchQuery.value,
    text: searchTextQuery.value,
  };
  if (hasText && !hadText) {
    delete params.sort;
  } else if (!hasText && hadText) {
    params.sort = SortOption.DOWNLOADS;
  }
  router.push(`/search?q=${JSON.stringify(params)}`);
};

const clearSearch = () => {
  searchTextQuery.value = '';
  handleSearchInput();
};

const searchQuery = useSearchQueryString();
onMounted(() => {
  searchTextQuery.value = searchQuery.value.text;
});
// Make sure search is in sync with the query string on navigation
watch(
  () => route?.path,
  () => {
    searchTextQuery.value = searchQuery.value.text;
    skillsetsStore.clearSearchResults();
  },
);

/**
 * Handle user logout
 */
const handleLogout = async () => {
  await authStore.logout();
  router.push('/login');
};

const goToSkills = () => {
  const SKILLS_PARAMS: SearchParams = {
    text: '',
    contentType: [ContentType.SKILL],
  };

  router.push(`/search?q=${JSON.stringify(SKILLS_PARAMS)}`);
};

const goToSubagents = () => {
  const SUBAGENTS_PARAMS: SearchParams = {
    text: '',
    contentType: [ContentType.SUBAGENT],
  };

  router.push(`/search?q=${JSON.stringify(SUBAGENTS_PARAMS)}`);
};

const goToSkillsets = () => {
  const SKILLSETS_PARAMS: SearchParams = {
    text: '',
    contentType: [ContentType.SKILLSET],
  };

  router.push(`/search?q=${JSON.stringify(SKILLSETS_PARAMS)}`);
};

const skillsetsLinkClass = computed(() =>
  searchQuery.value.contentType?.length === 1 &&
  searchQuery.value.contentType[0] === ContentType.SKILLSET
    ? 'router-link-active'
    : '',
);
const skillsLinkClass = computed(() =>
  searchQuery.value.contentType?.length === 1 &&
  searchQuery.value.contentType[0] === ContentType.SKILL
    ? 'router-link-active'
    : '',
);
const subagentsLinkClass = computed(() =>
  searchQuery.value.contentType?.length === 1 &&
  searchQuery.value.contentType[0] === ContentType.SUBAGENT
    ? 'router-link-active'
    : '',
);

const goToPendingReview = () => {
  const params: SearchParams = {
    text: '',
    includePending: true,
    pendingOnly: true,
  };
  router.push(`/search?q=${JSON.stringify(params)}`);
};

const namespace = computed(() => configStore.namespace);
</script>

<template>
  <header class="app-header">
    <div class="nav-wrapper">
      <nav>
        <a
          v-if="showSkillsetsLink"
          :class="skillsetsLinkClass"
          @click="goToSkillsets"
        >
          Skillsets
        </a>
        <a v-if="showSkillsLink" :class="skillsLinkClass" @click="goToSkills">
          Skills
        </a>
        <a
          v-if="showSubagentsLink"
          :class="subagentsLinkClass"
          @click="goToSubagents"
        >
          Subagents
        </a>
        <router-link v-if="showInternalRegistryLink" to="/for-teams">
          For Teams
        </router-link>
        <router-link to="/docs/building-a-skillset">Docs</router-link>
        <div class="spacer" />
        <router-link v-if="!isAuthenticated" class="button-link" to="/login">
          <button class="login-btn">Login</button>
        </router-link>
        <template v-else>
          <span v-if="namespace" class="namespace-badge">{{ namespace }}</span>
          <NavDropdown>
            <div class="dropdown">{{ userDisplayName }} &#9662;</div>
            <template #contents>
              <router-link v-if="isAuthenticated" to="/publish">
                Publish
              </router-link>
              <router-link v-if="isAuthenticated" to="/builder">
                Build Skillset
              </router-link>
              <router-link v-if="showAdminLinks" to="/admin/users">
                Users
              </router-link>
              <a
                v-if="
                  isAuthenticated &&
                  configStore.isPrivateInstance &&
                  configStore.noriSessionsEnabled &&
                  configStore.namespace
                "
                :href="`https://sessions.${configStore.namespace}.noriskillsets.dev`"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sessions Dashboard
              </a>
              <router-link
                v-if="showAdminLinks || authStore.isAdmin"
                to="/admin/settings"
              >
                Settings
              </router-link>
              <a
                v-if="isAuthenticated && authStore.isAdmin"
                @click="goToPendingReview"
              >
                Pending Review
              </a>
              <router-link v-if="isAuthenticated" :to="userProfileUrl">
                Profile
              </router-link>
              <router-link v-if="isAuthenticated" to="/pending">
                Pending Submissions
              </router-link>

              <button
                v-if="isAuthenticated"
                class="logout-btn"
                @click="handleLogout"
              >
                Logout
              </button>
            </template>
          </NavDropdown>
        </template>
      </nav>
    </div>
    <div class="container">
      <router-link to="/" class="logo">
        <h1>
          <img
            v-if="configStore.orgCreatedLogo"
            :src="configStore.orgCreatedLogo"
            class="logo-icon custom-logo-icon"
            width="16"
            height="16"
            alt="Logo"
          />
          <svg
            v-else
            class="logo-icon"
            width="16"
            height="16"
            viewBox="0 0 32 32"
            fill="none"
          >
            <rect
              x="1"
              y="11"
              width="20"
              height="20"
              rx="2"
              fill="#065c0e"
              stroke="#000"
              stroke-width="1.5"
            />
            <rect
              x="11"
              y="1"
              width="20"
              height="20"
              rx="2"
              fill="#065c0e"
              stroke="#000"
              stroke-width="1.5"
            />
          </svg>
          Nori Skillsets
        </h1>
      </router-link>
      <div class="search-wrapper">
        <div class="search">
          <font-awesome-icon
            class="search-icon"
            icon="fa-solid fa-magnifying-glass"
          />
          <input
            v-model="searchTextQuery"
            type="text"
            placeholder="Search..."
            class="search-input"
            @input="handleSearchInput"
            @keydown.enter="handleSearch"
          />
          <font-awesome-icon
            v-if="searchTextQuery.length > 0"
            class="clear-search-icon"
            icon="fa-solid fa-xmark"
            @click="clearSearch"
          />
        </div>
        <span
          v-if="searchLoadingStore.isLoading"
          class="search-loading-indicator"
          role="status"
          aria-live="polite"
        >
          Loading...
        </span>
        <button @click="handleSearch">Search</button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  background: var(--background);
  color: var(--foreground);
  border-bottom: 1px solid var(--border);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  gap: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
}

.logo {
  flex-shrink: 0;
  text-decoration: none;
  color: var(--foreground);
}

.logo h1 {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  display: inline-flex;
  align-items: center;
}

.logo-icon {
  margin-right: 6px;
}

.custom-logo-icon {
  object-fit: contain;
}

.nav-wrapper {
  border-bottom: 1px solid var(--border);
}

nav {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 1.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 2rem;
}

nav a,
nav .dropdown {
  color: var(--muted-foreground);
  text-decoration: none;
  padding: 0;
  transition: color 0.15s;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.875rem;
  position: relative;
  cursor: pointer;
}

nav a::after {
  display: none;
}

nav a:hover,
nav a.router-link-active {
  color: var(--foreground);
}

.spacer {
  flex-grow: 1;
}

button {
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.15s;
  box-sizing: border-box;
  color: var(--muted-foreground);
  background: transparent;
  border-radius: 6px;
}

button:hover {
  background: var(--muted);
  color: var(--foreground);
}

.login-btn {
  background: var(--primary);
  color: var(--primary-foreground);
}

.login-btn:hover {
  background: var(--primary);
  opacity: 0.9;
  color: var(--primary-foreground);
}

.search-wrapper {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  gap: 0.5rem;
}

.search {
  position: relative;
  flex-grow: 1;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted-foreground);
  font-size: 0.875rem;
}

.clear-search-icon {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted-foreground);
  cursor: pointer;
  padding: 0.5rem;
  font-size: 0.875rem;
}

.clear-search-icon:hover {
  color: var(--foreground);
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 2.5rem;
  font-family: var(--font-body);
  font-size: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--background);
  transition: border-color 0.15s, box-shadow 0.15s;
}

.search-input::placeholder {
  color: var(--muted-foreground);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(24, 24, 27, 0.1);
}

.search-wrapper button {
  background: var(--primary);
  color: var(--primary-foreground);
  padding: 0.5rem 1rem;
}

.search-wrapper button:hover {
  opacity: 0.9;
  background: var(--primary);
  color: var(--primary-foreground);
}

.search-loading-indicator {
  color: var(--muted-foreground);
  font-size: 0.875rem;
  font-family: var(--font-body);
  white-space: nowrap;
}

.namespace-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: var(--font-body);
  background: var(--muted);
  color: var(--muted-foreground);
  border-radius: 4px;
  border: 1px solid var(--border);
}
</style>
